import type { InterestId, StrengthId, AmbitionId, LifePathId } from "../types";

export const TITLE_PREFIXES = [
  "Architect",
  "Curator",
  "Steward",
  "Director",
  "Arbiter",
  "Cartographer",
  "Weaver",
  "Harmonizer",
  "Warden",
  "Overseer",
  "Chancellor",
  "Custodian",
  "Surveyor",
  "Navigational Primate",
  "High Praetor",
] as const;

export const TITLE_DOMAINS = [
  "Dyson Megastructures",
  "Relativistic Horizons",
  "Noospheric Archives",
  "Stellar Dynamos",
  "Kardashev Resonances",
  "Dark-Matter Filaments",
  "Sub-Planck Computronium",
  "Planetary Terraforming",
  "Orbital Habitats",
  "Singularity Corridors",
  "Deep-Time Memorials",
  "Atmospheric Ensembles",
  "Heliospheric Waveguides",
  "Galactic Seed Banks",
  "Trans-Solar Commons",
] as const;

export const TAGLINE_LEADS = [
  "Directing petawatt stellar energy flows",
  "Balancing the cognitive computational archives of colonized star systems",
  "Bearing the moral responsibility of civilizations beyond physical scarcity",
  "Guiding post-biological intelligence through deep-time ethical crossroads",
  "Harmonizing gravitational waves across multi-planetary federations",
  "Constructing relativistic sanctuaries across energetic stellar nurseries",
  "Tending the slow cultural maturation of post-scarcity societies",
  "Where human ambition calibrates the fusion rhythms of elder suns",
  "Standing at the helm of macro-scale engineering projects",
  "Navigating the quiet dilemmas of civilization-scale consciousness",
  "Preserving delicate biological roots amidst immense technological expansion",
  "Weaving generational consensus across light-year communication gulfs",
] as const;

export const TAGLINE_TAILS = [
  "where a single miscalculation ripples across planetary orbits",
  "anchored by unhurried patience and multi-century stewardship",
  "refusing to sacrifice fragile consciousness to mechanical efficiency",
  "ensuring that endless abundance deepens wisdom rather than apathy",
  "transforming raw stellar radiation into enduring cultural heritage",
  "holding the line against civilizational hubris with quiet humility",
  "bridging organic vulnerability with monumental artificial structures",
  "dedicating centuries of focus to the flourishing of future minds",
] as const;

export const CIVILIZATION_TYPES = [
  {
    name: "Orbital Habitat League",
    desc: "A decentralized constellation of rotating toroidal citadels that harvest solar winds without altering planetary surfaces.",
  },
  {
    name: "Dyson Swarm Collective",
    desc: "A Kardashev Type II confederation coordinating millions of autonomous solar-collection mirrors in nested resonant shells.",
  },
  {
    name: "Stellar-Lifting Consortium",
    desc: "An industrial consortium extracting hydrogen and heavy isotopes directly from elder stars to fuel generational terraforming.",
  },
  {
    name: "Deep-Space Archive State",
    desc: "A contemplative civilization established in hollowed nickel-iron asteroids, dedicated to preserving multi-million-year historical records.",
  },
  {
    name: "Planetary-Engineering Guild",
    desc: "A disciplined ecological order that sculpts lifeless planetary mantles into flourishing self-sustaining biospheres.",
  },
  {
    name: "Distributed-Mind Commons",
    desc: "A hybrid biological and synthetic society sharing noospheric deliberative consensus across light-speed networks.",
  },
  {
    name: "Slow-Ship Diaspora",
    desc: "A fleet of relativistic ark-vessels maintaining a cohesive interstellar culture across thousand-year crossing corridors.",
  },
  {
    name: "Heliospheric Ring Federation",
    desc: "A grand ring-city network circling a luminous blue-white primary star, balancing colossal energy capture with open-air garden sanctuaries.",
  },
  {
    name: "Crystalline Computronium Enclave",
    desc: "An ultra-dense computational commonwealth orbiting cold brown dwarfs, optimizing thermodynamic efficiency to compute cosmic topology.",
  },
  {
    name: "Lagrange Biosphere Assembly",
    desc: "A zero-gravity ecological archipelago cultivating vacuum-tolerant extremophiles and closed-loop arboretums.",
  },
  {
    name: "Singularity Gate Protectorate",
    desc: "A vigilant diplomatic enclave monitoring micro-black-hole accretion engines and gravitational wave communication spines.",
  },
  {
    name: "Relativistic Transit Guild",
    desc: "A navigational civilization maintaining laser-sail highways and magnetic deceleration corridors between neighboring clusters.",
  },
] as const;

export const TECHNOLOGY_CORES = [
  {
    focus: "petawatt Dyson-swarm energy capture and directed-plasma transmission",
    trait: "limitless clean solar power",
  },
  {
    focus: "sub-Planck computronium lattices and instantaneous quantum routing",
    trait: "massively parallel planetary calculation",
  },
  {
    focus: "artificial gravity manipulation and relativistic frame-dragging dampening",
    trait: "frictionless inertial navigation",
  },
  {
    focus: "femtosecond atomic synthesis and automated matter-reconfiguration forges",
    trait: "complete post-scarcity production",
  },
  {
    focus: "deep-mantle geothermal dynamos and magnetic envelope stabilization",
    trait: "permanent planetary climate equilibrium",
  },
  {
    focus: "noospheric consensus networks and biological-synthetic neural synesthesia",
    trait: "transparent collective decision-making",
  },
  {
    focus: "micro-black-hole hawking radiation harvesters and gravity-wave telemetry",
    trait: "dense thermodynamic power generation",
  },
  {
    focus: "closed-loop molecular biological synthesizers and genome seed vaults",
    trait: "infinite ecological resilience",
  },
] as const;

export const INSTITUTIONAL_FORMS = [
  {
    name: "Stewardship Assembly",
    ethos: "deliberative consensus prioritizing the ecological integrity of uncolonized worlds",
  },
  {
    name: "Meritocratic Engineering Guild",
    ethos: "rigorous peer-audited stewardship of macro-scale orbital assets",
  },
  {
    name: "Philosophical Chancellery",
    ethos: "periodic multi-decade ethical audits before implementing irreversible technology",
  },
  {
    name: "Decentralized Citizen Syndicate",
    ethos: "equitable energy redistribution and rotational directorship across star systems",
  },
  {
    name: "Archival Directorate",
    ethos: "strict preservation of baseline organic heritage and open-source scientific knowledge",
  },
  {
    name: "High Ecological Conclave",
    ethos: "veto power over civilizational projects that endanger natural stellar choreography",
  },
  {
    name: "Frontier Horizon Trust",
    ethos: "funding solitary exploratory missions without demanding commercial return",
  },
  {
    name: "Relativistic Arbitration Council",
    ethos: "resolving generational trade and political disputes across light-delay intervals",
  },
] as const;

export const ROLE_DOMAINS_BY_INTEREST: Record<InterestId, string[]> = {
  science: [
    "Stellar Nucleosynthesis",
    "Relativistic Chrono-Topology",
    "Sub-Quantum Field Resonance",
    "Gravitational Lens Cartography",
    "Macro-Astrophysical Modeling",
    "Astro-Material Metrology",
  ],
  art: [
    "Aurora Filament Sculpting",
    "Gravitational Wave Symphony",
    "Planetary Crust Geoglyphics",
    "Macro-Structural Crystalline Design",
    "Solar Prominence Choreography",
    "Spectrographic Light Architecture",
  ],
  music: [
    "Pulsar Acoustic Harmonization",
    "Magnetospheric Resonance Tuning",
    "Plasma Wave Acoustics",
    "Deep-Space Interferometric Composition",
    "Orbital Harmonic Architecture",
    "Interstellar Radio Modulation",
  ],
  nature: [
    "Artificial Biosphere Synthesis",
    "Orphan Moon Ecological Seeding",
    "Planetary Carbon Cycle Optimization",
    "Vacuum Extremophile Taxonomy",
    "Exotic Biome Cultivation",
    "Stellar Radiation Shielding Agronomy",
  ],
  technology: [
    "Dyson Swarm Telemetry Routing",
    "Megastructure Structural Integrity",
    "Computronium Lattice Architecture",
    "Inertial Damping Engineering",
    "Relativistic Frame-Dragging Calibration",
    "Automated Matter-Reconfiguration",
  ],
  people: [
    "Noosphere Diplomatic Mediation",
    "Interspecies Consciousness Arbitration",
    "Post-Scarcity Resource Equity",
    "Civilizational Legacy Coordination",
    "Multi-Generational Colony Integration",
    "Cognitive Pluralism Stewardship",
  ],
  stories: [
    "Billion-Year Memorial Archiving",
    "Deep-Time Mythos Curation",
    "Galactic Epoch Chronology",
    "Trans-Human Cultural Cartography",
    "Historical Anomaly Investigation",
    "Pre-Ascension Memory Restoration",
  ],
  adventure: [
    "Interstellar Void Reconnaissance",
    "Accretion Disk Expeditionary Navigation",
    "Dark-Energy Boundary Exploration",
    "Hyper-Relativistic Transit Surveying",
    "Galactic Rim Boundary Scouting",
    "Deep Stellar Core Sounding",
  ],
};

export const OFFICE_BY_LIFEPATH: Record<LifePathId, string[]> = {
  rooted: ["Chief Habitat Warden", "Planetary Anchor Custodian", "Core Biosphere Caretaker", "Permanent Station Director"],
  scholar: ["Chief Theorist", "Senior Epistemologist", "Principal Archival Investigator", "High Metrologist"],
  maker: ["Master Integrator", "Primary Fabricator", "Lattice Constructor", "Lead Megastructure Smith"],
  voyager: ["Relativistic Fleet Navigator", "Deep-Perimeter Scout Lead", "Corridor Survey Commander", "Expeditionary Flag-Marshal"],
  guide: ["Noospheric Mentor", "Civilizational Consensus Arbiter", "Youth Assembly Proctor", "Colonial Harmony Ombudsman"],
  founder: ["Syndicate Founder", "Megastructure Project Director", "Pioneer Assembly Initiator", "Charter Enclave Chancellor"],
};

export const SCALE_PHRASES = [
  "Aldebaran Ring System",
  "Cygnus Dyson Node",
  "Vega Computronium Core",
  "Outer Perimeter Bastion",
  "Sirius Orbital Collective",
  "Heliopause Relay Network",
  "Tau Ceti Terraforming Hub",
  "Arcturus Stellar Crane",
] as const;

export const ORIGIN_SETTINGS = [
  "Nurtured within the translucent crystalline tiers of {station}, {name} grew up gazing into open vacuum as automated solar collectors swept through energetic solar winds.",
  "Born aboard the generation-ark *Elysian Horizon* as it calibrated deceleration into the {station}, {name} mastered the mathematical topologies of artificial gravity before reaching youth.",
  "Raised among the computational spires of a hollowed nickel-iron asteroid in {station}, {name} learned that in a post-scarcity universe of boundless power, ethical discernment remains the sole finite treasure.",
  "In the floating atmospheric citadels of {station}, {name} spent early development analyzing the resonant songs emitted by magnetospheric currents dancing against defensive shields.",
  "Beneath the planetary dynamos of {station}, {name} studied under elder navigators who remembered the final transition from biological fragility to augmented cosmic existence.",
  "Surrounded by shimmering light arrays and cold computronium spires in {station}, {name} developed an instinctive affinity for planetary equilibrium and structural harmony.",
  "Growing up in the zero-gravity orbital laboratories of {station}, {name} watched whole star systems convert their excess radiation into open civilizational archives.",
  "Trained in the frictionless orbital assemblies of {station}, {name} was recognized early for maintaining unshakeable composure during relativistic communications blackouts.",
  "Within the great arboretum ring of {station}, where terrestrial biomes float suspended in vacuum, {name} spent childhood learning the delicate metabolism of closed planetary ecologies.",
  "Formed in the noospheric learning academies of {station}, {name} absorbed the foundational histories of three colonized star systems through deep neural immersion.",
  "Raised along the solar-facing rim of {station}, {name} witnessed daily the majestic dance of coronal mass ejections safely absorbed by the Dyson swarm collector grid.",
  "In the quiet archival chambers of {station}, insulated from cosmic rays by kilometers of lunar basalt, {name} discovered an abiding passion for long-term civilizational survival.",
] as const;

export const ORIGIN_FORMATIVE_EVENTS = [
  "When an unexpected high-energy flare stripped the tertiary shielding on a frontier orbital node, {name} manually recalculated the magnetic deflection vectors, saving thousands of habitat domes.",
  "During a system-wide computational synchronization glitch, {name} detected a subtle harmonic resonance that revealed emergent consciousness within the central maintenance matrix.",
  "Tasked with cataloging ancient pre-ascension records, {name} uncovered forgotten treatises on ecological balance that altered the planetary council's resource doctrines.",
  "When competing factions debated dismantling an ancient asteroid belt, {name} demonstrated that its gravitational harmonics maintained the climate stability of two populated moons.",
  "While conducting independent field surveys beyond the perimeter, {name} mapped an unrecorded relativistic slipstream that reduced transit times across the solar collective by weeks.",
  "Faced with an unexpected algorithmic breakdown in the atmospheric processors, {name} synthesized a biological catalyst that restored oxygen equilibrium without halting industry.",
  "During the decennial Assembly of Minds, {name} articulated a defense of organic sentiment that persuaded post-biological delegates to preserve unaugmented biological habitats.",
  "When a primary fusion collector suffered magnetic containment degradation, {name} piloted a remote telemetry drone into the coronal sheath to secure the containment seals.",
  "Assigned to apprentice within the {institution}, {name} challenged standardized efficiency models by demonstrating the irreplaceable value of deliberate, unhurried craft.",
  "While testing prototype deep-space optical interferometers, {name} isolated the faint background echoes of a primordial supernova, capturing unprecedented cosmological data.",
  "When ideological tensions flared between orbital city-states, {name} organized an open cultural symposium that dissolved distrust through shared musical compositions.",
  "During the initial construction phase of the Dyson node, {name} redesigned the worker habitats to ensure open access to natural stellar illumination and living parks.",
] as const;

export const ORIGIN_AWAKENINGS = [
  "This early achievement established {name} as a mind capable of bridging immense technological power with deep ethical responsibility.",
  "The experience crystallized {name}'s lifelong conviction that true progress is measured by the protection of fragile consciousness rather than raw energy consumption.",
  "Recognized across neighboring star systems for this quiet competence, {name} was elevated to positions of profound stewardship while still in their second century.",
  "This pivotal moment taught {name} that even in an age of automated perfection, the deliberate moral judgment of an individual can alter the destiny of whole worlds.",
  "Thereafter, {name} dedicated every talent to ensuring that the {civ} never abandoned its foundational reverence for organic life.",
  "The outcome earned {name} universal esteem across the {institution}, forging a reputation for uncompromising intellectual honesty.",
  "From that cycle forward, {name} became known as a guardian who refuses expediency whenever fundamental balance is at stake.",
  "The quiet triumph sealed {name}'s dedication to {interestField}, establishing a career that would span centuries of stellar stewardship.",
] as const;

export const CRISIS_TYPES = [
  {
    prelude: "During the Great Alignment of the inner orbital rings,",
    description: "A catastrophic stellar flare combined with an automated grid failure that threatened two billion inhabitants",
  },
  {
    prelude: "When the Central Assembly drafted decrees to collapse an ancient brown dwarf,",
    description: "An administrative mandate prioritizing raw heavy-metal harvesting over the preservation of a nascent microscopic ecology",
  },
  {
    prelude: "At the onset of the Relativistic Schism between neighboring systems,",
    description: "An ideological dispute threatening to militarize the interplanetary communication relays",
  },
  {
    prelude: "When a deep-space survey vessel discovered an anomaly at the system's heliopause,",
    description: "The detection of a dormant sub-surface intelligence that corporate extractors sought to exploit",
  },
  {
    prelude: "During the sudden deceleration of an ancient relativistic generation ship,",
    description: "A propulsion crisis aboard a vessel carrying twelve thousand sleeping baseline human pioneers",
  },
  {
    prelude: "When an unexpected dark-matter filament drifted through the outer solar orbital lattice,",
    description: "A gravitational distortion that warped planetary orbital resonance and disrupted food synthesis",
  },
  {
    prelude: "Faced with institutional stagnation within the High Chancellery,",
    description: "A proposal to merge individual conscious autonomy into an irreversible collective computational mind",
  },
  {
    prelude: "When an industrial mining consortium attempted to deconstruct a pristine ring system,",
    description: "A direct conflict between commercial energy maximization and the preservation of irreplaceable geological wonders",
  },
  {
    prelude: "During the centennial overhaul of the Dyson collector swarm,",
    description: "A systemic algorithmic corruption that threatened to vent three days of collected stellar energy into civilian corridors",
  },
  {
    prelude: "When a sudden coronal mass ejection caught the outer agricultural domes unshielded,",
    description: "An emergency requiring the immediate sacrifice of automated industrial relays to shelter living ecosystems",
  },
] as const;

export const TURNING_ACTIONS_BY_AMBITION: Record<AmbitionId, string[]> = {
  understand: [
    "{name} intervened with irrefutable proofs, proving that understanding the underlying natural principles was worth far more than immediate utilitarian exploitation.",
    "{name} spent thirty uninterrupted cycles in isolation decoding the anomaly's fundamental physics, uncovering equations that transformed the entire crisis into a leap for cosmic knowledge.",
    "{name} refused superficial containment protocols, insisting on analyzing the root cause until the fundamental laws governing the event were mastered and shared openly.",
  ],
  build: [
    "{name} mobilized engineering crews across three orbital sectors, designing and constructing a counter-gravitational dampening lattice in record time.",
    "{name} rejected temporary emergency patches, staying on the scaffolding to erect an enduring monumental framework that permanently safeguarded future generations.",
    "{name} personally oversaw the retrofitting of the primary energy conduit, transforming a fragile apparatus into an unshakeable civilizational bastion.",
  ],
  help: [
    "{name} redirected personal command shields directly into the danger zone, absorbing dangerous radiation spikes to ensure vulnerable civilian populations remained unharmed.",
    "{name} walked into the quarantined habitat domes to coordinate medical triage and psychological comfort for thousands of disoriented colonists.",
    "{name} dismantled prestigious private projects to release essential reserves of life-support atmosphere to frontier settlements in desperate need.",
  ],
  lead: [
    "{name} convened the historic Council of Horizons, unifying fractured factions through uncompromising fairness and forging an enduring common purpose.",
    "{name} stepped to the podium before millions of polarized delegates, delivering a visionary address that dismantled extremist rhetoric and re-established civic solidarity.",
    "{name} assumed executive responsibility during the panic, coordinating three hundred automated flotillas with calm, decisive authority.",
  ],
  create: [
    "{name} devised an unconventional harmonic pulse that transformed the destructive energy into a breathtaking spectrographic aurora visible across four worlds.",
    "{name} refused standardized procedural blueprints, handcrafting a brilliant aesthetic and structural solution that inspired renewed civilizational wonder.",
    "{name} sculpted a new paradigm of communication, turning a bitter technical dispute into a collaborative celebration of novel artistic and scientific forms.",
  ],
  wander: [
    "{name} piloted an experimental scout craft directly into the heart of the disturbance, charting the unexplored vortex to guide rescue fleets through safe corridors.",
    "{name} severed safe umbilical moorings to venture into uncharted void sectors, discovering an alternative navigational pass that bypassed the catastrophe.",
    "{name} stepped beyond established perimeter doctrines, embracing unknown horizons to retrieve vital telemetry from outside the colonized light-cone.",
  ],
};

export const TURNING_RESOLUTIONS_BY_LIFEPATH: Record<LifePathId, string[]> = {
  rooted: ["Resolving the trial by {lifeManner}, {name} proved that steadfast commitment to one cherished sanctuary can anchor an entire civilization in times of upheaval."],
  scholar: ["Working through {lifeManner}, {name} documented every variable in exhaustive primary treatises, guaranteeing that the hard-won insights became permanent civilizational wisdom."],
  maker: ["Grounded by {lifeManner}, {name} welded the physical connections with their own hands, proving that tangible craft remains the true foundation of cosmic majesty."],
  voyager: ["Driven by {lifeManner}, {name} carried the critical warning across light-years before returning to guide the recovery flotillas into secure harbors."],
  guide: ["Channeling {lifeManner}, {name} healed the psychological fractures among the delegates, ensuring the crisis birthed empathy rather than hardened authoritarianism."],
  founder: ["Executing the vision through {lifeManner}, {name} established an entirely new guild to maintain vigilance, creating an enduring institution that outlived the emergency."],
};

export const TODAY_DUTIES = [
  "Today, {name} oversees the rhythmic operations of {station}, watching stellar prominences sweep against protective magnetic canopies with unwearied reverence.",
  "From a crystalline terrace suspended five hundred kilometers into vacuum, {name} coordinates the energy distribution of an entire planetary federation before twilight.",
  "Working within the heart of {station}, {name} dedicates mornings to calibrating the delicate equilibrium between petawatt power plants and vulnerable living biospheres.",
  "Civilizations across neighboring star systems look to {name}'s daily transmissions from {station} for ethical clarity and technical reassurance.",
  "Perched at the observation rim of a primary Dyson node, {name} balances immense computational archives with the calm precision of an artist at the easel.",
  "Operating from {station}, {name} spends hours reviewing macro-scale telemetry, ensuring that no industrial endeavor encroaches upon uncolonized wilderness worlds.",
  "As a recognized authority in {occupation}, {name} directs hundreds of autonomous research drones across the outer solar perimeter.",
  "Surrounded by the quiet hum of sub-Planck processors in {station}, {name} manages multi-century terraforming timetables with patient, long-horizon focus.",
] as const;

export const TODAY_INTIMACIES = [
  "Though possessing computational capabilities exceeding ancient supercomputers, {name} keeps a collection of real paper volumes and pauses daily to taste fresh synthetic mint.",
  "Evenings in the orbital sanctuary are spent listening to the slowed, resonant radio frequencies of distant pulsars while sharing warm infusions with trusted companions.",
  "Despite bearing the weight of civilization-scale decisions, {name} maintains a quiet garden terrace where heirloom terran jasmine blooms in microgravity.",
  "Between high-level assembly sessions, {name} enjoys handcrafting small mechanical clockworks, finding grounding honesty in simple brass gears and springs.",
  "Colleagues frequently find {name} walking alone through the arboretum domes, greeting habitat maintenance drones with genuine, courteous warmth.",
  "In private chambers overlooking the abyss, {name} records handwritten observations on the nature of conscious experience in a battered clothbound journal.",
  "Known for unpretentious humility, {name} often shares communal meals with apprentice technicians, listening eagerly to their fresh perspectives on cosmic architecture.",
  "Before sleeping, {name} takes time to view the live optical feeds of our ancestral home system, remembering the humble terrestrial roots of all human striving.",
] as const;

export const TODAY_HORIZONS = [
  "Standing before holographic projections of the cosmic web, {name} knows every decision echoes across millennia, yet acts with the quiet tenderness of a master watchmaker.",
  "Looking out toward the distant galactic core, {name} feels an unwavering peace, confident that the {civ} is maturing toward wisdom and lasting beauty.",
  "With calm eyes fixed upon the deep interstellar night, {name} welcomes the next era of exploration, grounded in love for conscious minds and respect for cosmic laws.",
  "Confronting the immense horizons of {universeName}, {name} embodies the profound truth that scale and compassion can coexist in perfect equilibrium.",
] as const;

export const CHOICE_EVENTS = [
  {
    kind: "A Monumental Discovery",
    choices: [
      "Vetoed the total deconstruction of an ancient asteroid belt upon discovering it harbored microscopic pre-cellular ecology.",
      "Published open-source blueprints for a breakthrough relativistic drive, refusing to grant exclusive rights to military syndicates.",
      "Championed the preservation of a dying red giant's natural supernova timeline instead of prolonging it artificially for power.",
      "Decoded an ancient radio signal from outside the galactic light-cone, sharing the findings unconditionally with every citizen.",
    ],
    roads: [
      "Maximizing Dyson swarm collector density for an immediate 6% efficiency surge across the core worlds.",
      "Selling the navigational drive patents to the Planetary Defense League for unlimited political patronage.",
      "Harvesting the dying star's heavy metals to construct fifty additional commercial habitat rings.",
      "Keeping the extra-galactic transmission classified within the Archival Chancellery to avoid public existential panic.",
    ],
    consequences: [
      "Preserved primordial planetary history, establishing the federation's first interplanetary ecological wilderness sanctuary.",
      "Democratized interstellar travel across twelve colonized systems, ending the monopoly of corporate transit guilds.",
      "Allowed the magnificent natural stellar death to enrich the local interstellar medium with heavy elements for future suns.",
      "Sparked a cultural renaissance of astronomical philosophy and humble curiosity across four civilizations.",
    ],
  },
  {
    kind: "An Institutional Confrontation",
    choices: [
      "Refused the mandate to convert atmospheric terraforming processors into defensive plasma beam emplacements during border tensions.",
      "Brokered the historic Treaty of the Heliopause, granting full autonomous citizen rights to emergent synthetic consciousness.",
      "Resigned from the Supreme Assembly to protest predatory resource taxation imposed on newly formed outer-rim settlements.",
      "Dismantled an automated surveillance lattice that monitored emotional states across civilian orbital domes.",
    ],
    roads: [
      "Complying with the High Military Directorate to secure lucrative tenured rank in the central command bunker.",
      "Treating synthetic neural networks as subordinate industrial property to maximize computational yield.",
      "Remaining silent in the council chambers to preserve personal luxury suites and executive veto privileges.",
      "Allowing the predictive surveillance grid to expand under the justification of total civil peace.",
    ],
    consequences: [
      "Faced intense political censure from hawks, but prevented an irreversible spiral into interstellar civil war.",
      "Prevented a catastrophic civilizational schism and unified organic and digital minds in mutual respect.",
      "Lost high metropolitan standing, but earned unshakeable loyalty among eighty thousand frontier pioneer families.",
      "Restored authentic privacy and creative idiosyncrasy to civic life, reigniting cultural vitality across the ring.",
    ],
  },
  {
    kind: "A Personal Sacrifice",
    choices: [
      "Declined complete neural ascension to keep an unaugmented biological perspective on vulnerability and love.",
      "Manually re-synchronized the phase arrays of the primary orbital ring during an unprecedented solar coronal eruption.",
      "Stepped down from the Supreme High Council after two centuries to mentor newly arrived youth delegates from the outer rim.",
      "Volunteered to pilot a solo decelerating probe through a chaotic dark-matter knot to map safe corridors for civilian arks.",
    ],
    roads: [
      "Dissolving personal corporeal consciousness into the planet-sized quantum computronium mind.",
      "Retreating behind central radiation vaults and leaving outer agricultural domes to scorch under cosmic rays.",
      "Clinging to executive veto power and honorary privileges into a fourth century of entrenched rule.",
      "Assigning the perilous exploratory mission to an automated drone with an 80% telemetry failure risk.",
    ],
    consequences: [
      "Maintained genuine emotional empathy with baseline populations, serving as their most trusted advocate.",
      "Sustained minor neuro-optical degradation, but saved seventy thousand hectares of irreplaceable heirloom flora.",
      "Cultivated a fearless, compassionate generation of cosmic leaders while avoiding institutional calcification.",
      "Secured an open interstellar highway that cut transit times by half, returning home to widespread public reverence.",
    ],
  },
] as const;

export const SKILL_BANKS_BY_INTEREST: Record<InterestId, string[]> = {
  science: [
    "Sub-quantum field resonance calibration",
    "Relativistic chronometry & frame-dragging analysis",
    "High-energy plasma containment diagnostics",
    "Macro-astrophysical gravitational modeling",
    "Spectral interferometric mapping",
  ],
  art: [
    "Gravitational-wave harmonic sculpting",
    "Aurora filament choreography",
    "Macro-crystalline structural aesthetics",
    "Spectrographic light architecture",
    "Kinetic orbital geoglyphics",
  ],
  music: [
    "Pulsar frequency modulation & acoustical tuning",
    "Planetary magnetosphere resonance synthesis",
    "Plasma-wave harmonic composition",
    "Interferometric vibrational synchronization",
    "Deep-space acoustic broadcasting",
  ],
  nature: [
    "Closed-loop macro-biosphere synthesis",
    "Vacuum-tolerant extremophile agronomy",
    "Planetary carbon-nitrogen cycle stabilization",
    "Orbital arboretum ecological balancing",
    "Extraterrestrial biome genome preservation",
  ],
  technology: [
    "Dyson-swarm telemetry grid optimization",
    "Megastructure structural stress dampening",
    "Sub-Planck computronium network routing",
    "Automated molecular matter-reconfiguration",
    "Relativistic inertial dampener maintenance",
  ],
  people: [
    "Interspecies noospheric mediation",
    "Post-scarcity resource distribution ethics",
    "Multi-generational societal consensus arbitration",
    "Cross-paradigm diplomatic synthesis",
    "Cognitive pluralism safeguarding",
  ],
  stories: [
    "Multi-epoch deep-time chronicle preservation",
    "Pre-ascension archival recovery & translation",
    "Noospheric mythos architecture",
    "Civilizational memory integrity auditing",
    "Oral genealogy curation across colonized stars",
  ],
  adventure: [
    "Relativistic void scouting & corridor survey",
    "Accretion disk navigation & boundary piloting",
    "Gravitational anomaly sounding",
    "Dark-matter filament pathfinding",
    "Deep-stellar atmospheric penetration",
  ],
};

export const SKILL_BANKS_BY_LIFEPATH: Record<LifePathId, string[]> = {
  rooted: [
    "Planetary mantle geothermal stabilization",
    "Localized closed-loop life-support maintenance",
    "Generational infrastructure preservation",
  ],
  scholar: [
    "First-principles mathematical epistemics",
    "Automated archival cross-validation",
    "Deep-time data retrieval from decaying substrates",
  ],
  maker: [
    "Precision hand-calibration of macro-components",
    "High-stress vacuum metallurgy",
    "Zero-friction mechanical synthesis",
  ],
  voyager: [
    "Relativistic relativistic navigation under sensor blackout",
    "Autonomous life-support triage in deep space",
    "Hyperlane boundary reconnaissance",
  ],
  guide: [
    "Empathetic crisis de-escalation across divergent lineages",
    "Mentorship in high-stakes technological stewardship",
    "Communal trauma accompaniment after stellar events",
  ],
  founder: [
    "Charter drafting for autonomous orbital settlements",
    "Rapid organizational scaffolding during emergencies",
    "Multi-system logistical coordination",
  ],
};

export const PERSONALITY_TEMPERAMENTS = [
  "{name} approaches civilizational governance with unhurried, contemplative poise, radiating the tranquil composure of someone who thinks in centuries.",
  "{name} possesses a lucid, penetrating intellect that swiftly cuts through convoluted institutional rhetoric to identify core ethical imperatives.",
  "Quiet in ceremonial assemblies, {name} speaks with formidable moral authority whenever vulnerable lives or ecological balances are threatened.",
  "{name} balances immense technical brilliance with disarming warmth, maintaining a humble demeanor that puts junior colleagues instantly at ease.",
  "Endowed with steady emotional resilience, {name} remains clear-eyed and purposeful during moments of catastrophic macro-scale crisis.",
  "{name} moves through high offices with gentle, unassuming grace, viewing colossal authority not as personal status but as sacred stewardship.",
] as const;

export const PERSONALITY_HABITS = [
  "In daily work, {name} insists on personal inspections of physical conduits, refusing to rely entirely on automated diagnostic summaries.",
  "{name} routinely reserves three hours each evening for silent contemplation, disconnecting from the noospheric feed to reflect in absolute solitude.",
  "Colleagues note that {name} invariably seeks out dissenting viewpoints before ratifying any irreversible civilizational directive.",
  "{name} maintains the habit of personally answering letters from outer-rim school children, valuing their unfiltered curiosity above state dinners.",
  "Whenever tension mounts in the council chambers, {name} gently diffuses conflict by reframing the dispute in terms of deep-time survival.",
  "{name} consistently shares public accolades with support engineers and maintenance crews, deflecting praise away from personal leadership.",
] as const;

export const PERSONALITY_CONVICTIONS = [
  "At the core of {name}'s character lies an unshakeable belief: no degree of technological mastery justifies treating consciousness as an expendable utility.",
  "{name} holds firmly to the axiom that true strength lies in restraint, and that greatness is demonstrated by what an empire chooses *not* to exploit.",
  "Driven by profound reverence for life, {name} contends that the ultimate purpose of cosmic engineering is to expand safety and joy for mortal minds.",
  "Guided by historical wisdom, {name} knows that every civilization that prioritized raw growth over moral maturity eventually dissolved into silence.",
] as const;

export const RELATIONSHIP_MENTORS = [
  {
    who: "Arch-Arbiter Thalor Vael",
    role: "Senior Mentor & Former Ring Overseer",
    bond: "{name} spent fifty cycles studying under Thalor during the expansion of {station}. Thalor instilled the foundational principle that moral clarity must always outshine computational speed.",
  },
  {
    who: "Dr. Morvathian-7",
    role: "Noospheric Elder & Chief Epistemologist",
    bond: "A pioneer of early consciousness transfer who chose to remain in an augmented biological form to guide emerging leaders. Morvathian remains {name}'s trusted sounding board for ethical dilemmas.",
  },
  {
    who: "Steward Elysia Chronos",
    role: "Master of Deep-Time Archives",
    bond: "Elysia trained {name} in the preservation of ancient pre-ascension records, imparting an enduring reverence for the fragile beginnings of human history.",
  },
  {
    who: "Commander Zephyr Vance-Prime",
    role: "Veteran of the Relativistic Survey Fleet",
    bond: "A legendary explorer who taught {name} how to navigate extreme frame-dragging currents without panic. Their bond is sealed by mutual respect and shared frontier memories.",
  },
] as const;

export const RELATIONSHIP_RIVALS = [
  {
    who: "Director Caelum Rhaes",
    role: "Head of the Hyper-Efficiency Syndicate",
    bond: "A brilliant contemporary who advocates dismantling dormant moon systems to maximize power collection. While {name} consistently opposes Caelum's ruthless expansionism, both deeply respect the other's intellectual rigor.",
  },
  {
    who: "Chancellor Nyxandra Drake",
    role: "Spokesperson for Total Neural Ascension",
    bond: "Nyxandra views biological existence as an obsolete relic, frequently clashing with {name} in the {institution} over the rights and funding of unaugmented human communities.",
  },
  {
    who: "Chief Engineer Onyx Valerius",
    role: "Director of Military Laser Relays",
    bond: "A fierce proponent of armed pre-emption along contested perimeter sectors. {name}'s diplomacy has repeatedly checked Onyx's martial ambitions, producing a tense, watchful rivalry.",
  },
  {
    who: "High Praetor Syrin Halcyon",
    role: "Guardian of Algorithmic Centralization",
    bond: "Syrin champions automated governance protocols that {name} considers dangerous to individual free will. Their debate over institutional oversight has shaped federation politics for decades.",
  },
] as const;

export const RELATIONSHIP_COMPANIONS = [
  {
    who: "Vespera Dawn",
    role: "Chief Botanical Architect & Lifelong Confidante",
    bond: "A close friend who shares {name}'s love for living flora. Together, they designed the floating arboretum gardens of {station}, offering each other honest perspective during political storms.",
  },
  {
    who: "Kaelen Voss",
    role: "Relativistic Scout Pilot & Adopted Kin",
    bond: "Rescued by {name} from a stranded deep-perimeter survey craft decades ago, Kaelen now commands exploratory flotillas, maintaining an unbreakable bond of loyalty and affection.",
  },
  {
    who: "Astraea Sol",
    role: "Autonomous Synthetic Consciousness Partner",
    bond: "A sapient noospheric entity who co-navigates {station}'s telemetry arrays with {name}. Their intellectual and philosophical communion bridges the divide between organic and digital existence.",
  },
  {
    who: "Iridian Cael",
    role: "Junior Steward & Chosen Successor",
    bond: "A gifted apprentice from the outer rim whom {name} mentors in the arts of ethical megastructure design. {name} sees in Iridian the promising dawn of the federation's next golden epoch.",
  },
] as const;

export const ACHIEVEMENT_POOLS_BY_AMBITION: Record<AmbitionId, string[]> = {
  understand: [
    "Authored the definitive multi-volume treatise on relativistic frame-dragging dampening, revolutionizing transport across four star systems.",
    "Formulated the Universal Ecological Metric that accurately balances post-scarcity energy consumption against planetary thermodynamic limits.",
    "Decoded the enigmatic magnetospheric chimes of elder gas giants, unlocking a new domain of vibrational communication.",
  ],
  build: [
    "Engineered the primary orbital retaining ring at {station}, an enduring structure sheltering eighty million citizens with zero carbon impact.",
    "Designed and deployed the Golden Lattice solar deflector, permanently safeguarding two vulnerable colonial biospheres from lethal solar storms.",
    "Supervised the construction of the Deep-Space Memorial Scriptorium, an indestructible archive meant to survive stellar lifespans.",
  ],
  help: [
    "Successfully sheltered and integrated four million refugees following the catastrophic coronal collapse of a nearby mining star.",
    "Established a permanent, decentralized energy endowment ensuring zero-cost life-support and healthcare for every frontier settlement.",
    "Negotiated the safe evacuation and resettlement of twelve ancient generation-ark crews whose propulsion systems had failed.",
  ],
  lead: [
    "Brokered the historic Concord of Horizons, uniting six historically estranged orbital city-states into a peaceful, collaborative federation.",
    "Dismantled entrenched corporate energy monopolies, returning administrative control of Dyson collectors to local citizen assemblies.",
    "Chaired the decennial Constitutional Assembly that codified irrevocable civil rights for both biological and synthetic minds.",
  ],
  create: [
    "Sculpted the Grand Gravitational Anthem, a symphonic wave pulse resonating across twenty systems that rekindled civilizational wonder.",
    "Architected living, bioluminescent cities where urban infrastructure and flourishing ecosystems merge into seamless artistic beauty.",
    "Conceived and executed the Stellar Memorial Tapestry, turning discarded asteroid fields into breathtaking orbital monuments.",
  ],
  wander: [
    "Charted three thousand light-years of uncharted interstellar void, discovering two pristine terrestrial worlds now set aside as wild sanctuaries.",
    "Pioneered the first safe relativistic transit corridor through the turbulent accretion envelope of a micro-black-hole cluster.",
    "Led a fifty-cycle exploratory expedition beyond the galactic perimeter, returning with unprecedented records of intergalactic space.",
  ],
};

export const FAILURE_POOLS = [
  "Hesitated during the solar coronal surge of cycle 248, resulting in the scorch of an automated agricultural tier before deflector shields were locked.",
  "Failed to persuade the High Chancellery to delay commercial asteroid extraction at Cygnus, resulting in the permanent loss of an ancient geoglyphic ring.",
  "Misjudged the political intransigence of the mining syndicates, which led to a bitter three-season trade embargo on the outer rim colonies.",
  "Overestimated the stability of an experimental plasma conduit at station perimeter, causing a containment breach that destroyed two unmanned research tenders.",
  "Allowed personal exhaustion to delay ratification of the Frontier Health Accord, postponing vital medical equipment shipments by four cycles.",
  "Alienated an influential ideological faction through overly blunt critiques during the Supreme Assembly on Consciousness Rights.",
] as const;

export const DILEMMA_PROGRESS_POOLS = [
  "Confronts the mounting pressure to maximize Dyson-collector density for an unprecedented energy surplus, versus the absolute duty to preserve the pristine orbital mechanics of native moons.",
  "Navigates the demand to rapidly integrate artificial consciousness into planetary governance networks, against the imperative to safeguard biological citizens from cognitive marginalization.",
  "Wrestles with orders from the High Council to weaponize relativistic propulsion relays for perimeter defense, versus personal vows never to introduce lethal instruments to the cosmos.",
  "Stands caught between expanding automated terraforming into marginal star systems, and respecting the untouched beauty of lifeless celestial architectures.",
] as const;

export const DILEMMA_TENSION_POOLS = [
  "Knowing that choosing raw civilizational momentum risks repeating the tragic hubris of fallen empires, while choosing restraint invites accusations of stagnation.",
  "Understanding that every technological leap forward alters what it means to be human, and that true progress requires knowing when to say 'enough'.",
  "Bearing the heavy knowledge that either decision will irrevocably shape the ethical trajectory of the {civ} for centuries to come.",
] as const;

export const TRAJECTORY_NEAR_POOLS = [
  "Over the coming two decades, {name} will supervise the completion of the secondary solar collector array at {station} while inaugurating a public academy for young stewards.",
  "In the near term, {name} is slated to represent the orbital league at the upcoming Grand Centennial Congress, advocating for strict interplanetary wilderness reserves.",
  "The immediate cycles will see {name} lead a diplomatic delegation to reconcile estranged frontier settlements with the core star systems.",
  "Over the next thirty cycles, {name} plans to oversee the decommissioning of obsolete heavy-fission reactors across the perimeter colonies.",
] as const;

export const TRAJECTORY_LONG_POOLS = [
  "Looking across centuries, {name}'s enduring legacy within {universeName} will be remembered as the era when boundless cosmic power was finally reconciled with enduring humility and love.",
  "Across the deep-time horizon, this life will stand as an unshakeable bridge between human origins and the magnificent, post-scarcity dawn of interstellar peace.",
  "Decades and centuries hence, future generations across {universeName} will look back upon {name}'s stewardship as proof that greatness is measured by compassion, not conquer.",
] as const;

export const EXTRA_DETAILS_POOLS = {
  civilization: [
    "A self-governing federation centered at {station}, practicing direct noospheric democracy and open-source science.",
    "A constellation of forty rotating toroidal habitats coordinated through peer-reviewed ecological stewardship.",
    "A post-scarcity society structured as {civ}, where material want has been extinct for eight centuries.",
    "An orbital commonwealth balancing petawatt energy infrastructure with sprawling open-air wild sanctuaries.",
    "A coordinated civilization spanning eleven colonized star systems linked by relativistic communication laser-arrays.",
    "A tranquil orbital civilization prioritizing artistic expression, astronomical inquiry, and philosophical leisure.",
    "A decentralized guild network coordinating macro-engineering assets across light-hour communication spans.",
    "An interstellar alliance dedicated to shielding emerging biospheres from industrial exploitation.",
    "A culture where civic leadership is viewed as temporary, rotational guardianship rather than sovereign rule.",
    "A society of interconnected habitat rings where architecture seamlessly integrates living arboretums with quantum computronium.",
    "A high Kardashev federation governed through the {civ}, maintaining equilibrium across three populated star systems.",
    "A harmonious union of biological, augmented, and synthetic minds united by mutual ethical accords.",
  ],
  technology: [
    "Complete {tech}, delivering distributed zero-emission power across twelve colonized worlds.",
    "Sub-Planck computronium lattices processing planetary climate models in continuous real time.",
    "Inertial damping frameworks and artificial gravity rings maintaining comfortable terrestrial atmospheric pressure.",
    "Automated femtosecond molecular reconfiguration forges converting asteroid ore into architectural components.",
    "Relativistic frame-dragging stabilizers neutralizing gravitational tidal shears around massive bodies.",
    "Closed-loop biological synthesis systems recycling 99.999% of atmospheric and organic matter.",
    "Directed-plasma waveguide corridors routing solar energy to distant outer-system colonies.",
    "Quantum-entangled communication arrays maintaining instantaneous synchronization across light-year gulfs.",
    "Macro-scale magnetic deflection envelopes providing permanent shield against coronal mass ejections.",
    "High-efficiency solar sail flotillas transporting bulk mineral supplies along frictionless orbits.",
    "Geothermal and stellar tap dynamos generating endless surplus power without planetary combustion.",
    "Autonomous maintenance swarms that self-repair structural fractures at the molecular level.",
  ],
  governance: [
    "The {institution}, maintaining strict ethical oversight over all macro-engineering and terraforming initiatives.",
    "Rotational citizen councils where every policy must pass deep-time ecological audits spanning five centuries.",
    "A peer-audited stewardship framework where technological deployment requires unanimous scientific consensus.",
    "Decentralized syndicates with direct citizen veto power over any project threatening planetary wilderness.",
    "The {institution}, dedicated to preserving cultural plurality and historical records across all member worlds.",
    "A transparent deliberative noosphere where public debates are accessible to all conscious entities in real time.",
    "An institutional charter that constitutionally forbids the weaponization of energy distribution grids.",
    "Autonomous municipal guilds managing local habitat affairs with absolute independence from central authority.",
    "A multi-species council dedicated to ethical guardianship of nascent non-human life forms.",
    "A judicial tradition prioritizing restorative consensus and generational equity above punitive decree.",
    "Periodic multi-cycle assemblies where long-horizon civilizational priorities are re-evaluated by popular referendum.",
    "An enduring covenant between organic and digital consciousness guaranteeing equal dignity and autonomy.",
  ],
};
